import React, { useState, useEffect } from "react"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { PageHeader } from "@/components/ui/page-header"
import { EmptyState } from "@/components/ui/empty-state"
import { Button } from "@/components/ui/button"
import { toastSuccess, toastError } from "@/lib/toast"
import { UserAddressDto, UserAddressCreateDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { MapPin, Plus, Loader2, ChevronLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet"
import { type UserAddressFormData } from "@/lib/validationSchemas"
import { AddressCard } from "@/components/profile/AddressCard"
import { AddressForm } from "@/components/profile/AddressForm"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLanguage } from "@/hooks/useLanguage"

const SavedAddresses = () => {
  const { t, language } = useLanguage()
  const text = t.savedAddresses
  const [addresses, setAddresses] = useState<UserAddressDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [editingAddress, setEditingAddress] = useState<UserAddressDto | null>(null)
  const navigate = useNavigate()
  const fetchAddresses = async () => {
    setIsLoading(true)
    try {
      const data = await apiClient.getUserAddresses()
      setAddresses(data)
    } catch (error) {
      console.error("Error fetching addresses:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAddresses()
  }, [])

  const onSubmit = async (values: UserAddressFormData) => {
    try {
      if (editingAddress) {
        await apiClient.updateAddress(editingAddress.id, {
          receiverName: values.receiverName,
          phone: values.phone,
          address: {
            street: values.address.street,
            externalNumber: values.address.externalNumber,
            internalNumber: values.address.internalNumber || "",
            neighborhood: values.address.neighborhood,
            city: values.address.city,
            state: values.address.state,
            zipCode: values.address.zipCode,
            country: "México",
          },
          isDefault: values.isDefault ?? false,
        })

        toastSuccess(text.successEdit)
      } else {
        await apiClient.createAddress(values as UserAddressCreateDto)
        toastSuccess(text.successAdd)
      }
      setIsSheetOpen(false)
      setEditingAddress(null)
      fetchAddresses()
    } catch (error) {
      console.error("Form submission error:", error)
      toastError(text.validationError)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(text.deleteConfirm)) return
    try {
      await apiClient.deleteAddress(id)
      toastSuccess(text.successDelete)
      fetchAddresses()
    } catch (error) {
      console.error("Error deleting address:", error)
      toastError("Error deleting address")
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await apiClient.setDefaultAddress(id)
      toastSuccess(text.successDefault)
      fetchAddresses()
    } catch (error) {
      console.error("Error setting default address:", error)
      toastError("Error setting default address")
    }
  }

  const openEditSheet = (address: UserAddressDto) => {
    setEditingAddress(address)
    setIsSheetOpen(true)
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground transition-colors duration-300">
      <Header />

      <main className="container mx-auto max-w-5xl flex-1 px-4 py-8">
        <div className="mb-10 flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 p-0 font-display tracking-widest text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            {text.back}
          </Button>
        </div>

        <PageHeader title={text.title} subtitle={text.subtitle}>
          <Sheet
            open={isSheetOpen}
            onOpenChange={(open) => {
              setIsSheetOpen(open)
              if (!open) setEditingAddress(null)
            }}
          >
            <SheetTrigger asChild>
              <Button className="h-12 rounded-lg bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90">
                <Plus className="mr-2 h-4 w-4" />
                {text.addAddress}
              </Button>
            </SheetTrigger>
            <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-background p-0 shadow-2xl sm:max-w-[500px]">
              <SheetHeader className="border-b bg-muted/5 p-6">
                <SheetTitle className="font-display text-2xl tracking-widest">
                  {editingAddress ? text.editAddress : text.addAddress}
                </SheetTitle>
              </SheetHeader>

              <ScrollArea className="flex-1">
                <div className="p-6 pb-10">
                  <AddressForm
                    initialData={editingAddress}
                    onSubmit={onSubmit}
                    onCancel={() => setIsSheetOpen(false)}
                    showFooter={false} // We will use SheetFooter instead
                  />
                </div>
              </ScrollArea>

              <SheetFooter className="border-t bg-muted/10 p-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsSheetOpen(false)}
                  className="font-display tracking-widest"
                >
                  {text.cancel}
                </Button>
                <Button
                  type="submit"
                  form="address-form"
                  className="h-12 rounded-lg bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
                >
                  {text.save}
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </PageHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
            <Loader2 className="mb-4 h-12 w-12 animate-spin opacity-20" />
            <p className="font-display text-xl tracking-widest opacity-20">{text.loading}</p>
          </div>
        ) : addresses.length === 0 ? (
          <EmptyState
            icon={MapPin}
            message={text.noAddresses}
            action={
              <Button
                onClick={() => setIsSheetOpen(true)}
                variant="outline"
                className="rounded-lg border-2 border-accent/20 font-display tracking-widest hover:border-accent"
              >
                {text.addAddress}
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                onEdit={openEditSheet}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}

export default SavedAddresses

